import { faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLiveQuery } from 'dexie-react-hooks'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { useGlobalModal } from '../Contexts/GlobalModalContext'
import ConflictResolutionModal from './ConflictResolutionModal'

function issueLabel(issue: SyncIssue): string {
  const entityName = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'patternBlast' ? 'blast'
    : issue.entity === 'loading' ? 'truck load'
    : issue.entity === 'geoRehandle' ? 'rehandle/crushing entry'
    : issue.entity === 'completeShift' ? 'shift completion'
    : issue.entity
  if (issue.status === SyncStatus.Conflict) return `A ${entityName} conflict: it was edited by someone else`
  if (issue.status === SyncStatus.ShiftLocked) return `A ${entityName} change was rejected: shift is locked`
  if (issue.status === SyncStatus.Error) return `A ${entityName} change failed: ${typeof issue.serverState === 'string' ? issue.serverState : 'server error'}`
  return `A ${entityName} change could not be applied: record not found`
}

async function dismissIssue(id: number) {
  await offlineDb.syncIssues.delete(id)
}

async function dismissAll() {
  await offlineDb.syncIssues.clear()
}

const SyncIssuesBanner = () => {
  const issues = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), []) ?? []
  const { showModal, hideModal } = useGlobalModal()

  const openConflictModal = (issue: SyncIssue) => {
    showModal({
      content: () => (
        <ConflictResolutionModal issue={issue} onClose={hideModal} />
      ),
      tailwindMaxWidthClass: 'max-w-2xl',
    })
  }

  if (issues.length === 0) return null

  return (
    <div className="w-full flex flex-col gap-1 px-4 py-2 short:py-1 bg-theme-error text-theme-primary-contrast text-sm border-b border-theme-error-hover shrink-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <FontAwesomeIcon icon={faTriangleExclamation} className="shrink-0" />
          <span>{issues.length} sync {issues.length === 1 ? 'issue' : 'issues'} need your attention</span>
        </div>
        <button
          onClick={dismissAll}
          className="text-xs underline underline-offset-2 shrink-0 hover:opacity-80"
        >
          Dismiss all
        </button>
      </div>
      <ul className="flex flex-col gap-0.5 pl-6">
        {issues.map(issue => (
          <li key={issue.id} className="flex items-center justify-between gap-2">
            <span className="text-xs opacity-90">{issueLabel(issue)}</span>
            <div className="flex items-center gap-2 shrink-0">
              {issue.status === SyncStatus.Conflict && (
                <button
                  onClick={() => openConflictModal(issue)}
                  className="text-xs underline underline-offset-2 hover:opacity-80"
                >
                  Resolve
                </button>
              )}
              <button
                onClick={() => dismissIssue(issue.id!)}
                aria-label="Dismiss"
                className="hover:opacity-80"
              >
                <FontAwesomeIcon icon={faXmark} className="text-xs" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SyncIssuesBanner
